/**
 * The sidebar's behaviour on the markup the page already has: its theme
 * toggle, its visibility and mini state, and its collapsible submenus.
 */
import { translate } from '../../i18n';
import { Signal } from '@crown/signal';
import { effect } from '../../signal/effect';
import type { ActionHandler, CleanupFn, HTMLSignalElement } from '../types';

const SIDEBAR_COLLAPSE_TOGGLE_UTILITY = 'sidebar-collapse-toggle';

const NAV_COLLAPSIBLE_TRIGGER_UTILITY = 'nav-collapsible-trigger';
const NAV_COLLAPSIBLE_ICON_UTILITY = 'nav-collapsible-icon';

const VISIBLE_CLASS = 'visible';
const HIDDEN_CLASS = 'hidden';
const MINI_CLASS = 'mini';
const EXPANDED_CLASS = 'expanded';

type SignalElementType = {
  mini: boolean;
  visible: boolean;
};

const COLLAPSED_KEY = 'sidebar-collapsed';

/** Storage can be blocked; a collapse then lasts only for this page. */
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  } catch {
    // Blocked storage: nothing to remember it in.
  }
}

export const init = (
  element: HTMLSignalElement<SignalElementType>,
): CleanupFn => {
  /*
    SAFETY: the only writer of this property is the page's chrome, which
    assigns an object with exactly these two members; an element built any
    other way has no `signal` at all, which is the `undefined` arm.
  */
  const existingSignals = element.signal as
    | {
        mini?: Signal.State<boolean>;
        visible?: Signal.State<boolean>;
      }
    | undefined;
  const isVisibleSignal: Signal.State<boolean> =
    existingSignals?.visible ?? new Signal.State(true);
  const isMiniSignal: Signal.State<boolean> =
    existingSignals?.mini ?? new Signal.State(false);
  element.signal = {
    mini: isMiniSignal,
    visible: isVisibleSignal,
  };
  const sidebarId = element.id;
  const isCollapsible = element.dataset.collapsible === 'true';
  const cleanupFunctions: CleanupFn[] = [];
  let pendingTransitionEnd: ((e: TransitionEvent) => void) | null = null;

  if (!sidebarId) {
    console.error('Sidebar init failed: Element ID is missing.');
    return () => {};
  }

  const collapseToggle = element.querySelector<HTMLButtonElement>(
    `.${SIDEBAR_COLLAPSE_TOGGLE_UTILITY}`,
  );
  const themeToggle = element.querySelector<HTMLButtonElement>(
    '.sidebar-theme-toggle',
  );
  const collapsibleTriggers = element.querySelectorAll<HTMLButtonElement>(
    `.${NAV_COLLAPSIBLE_TRIGGER_UTILITY}`,
  );

  const applyTheme = (theme: 'light' | 'dark') => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    root.classList.toggle('light', theme === 'light');
    root.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
    updateThemeToggle(theme === 'dark');
  };

  const initializeTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;
    const theme: 'light' | 'dark' =
      savedTheme === 'light' || savedTheme === 'dark'
        ? savedTheme
        : prefersDark
          ? 'dark'
          : 'light';

    applyTheme(theme);
  };

  const updateThemeToggle = (isDark: boolean) => {
    if (!themeToggle) return;

    const wording = isDark ? 'Light mode' : 'Dark mode';
    const themeLabel = themeToggle.querySelector('.theme-label');
    if (themeLabel) {
      themeLabel.textContent = wording;
    }
    /*
      The name a reader hears has to contain the words a reader sees (2.5.3).
      A button labelled "Toggle theme" beside the words "Dark mode" cannot be
      operated by anybody who says what is written on it.
    */
    themeToggle.setAttribute('aria-label', wording);
  };

  const handleThemeToggle = (event: Event) => {
    event.preventDefault();
    const isDark = document.documentElement.classList.contains('dark');
    applyTheme(isDark ? 'light' : 'dark');
  };

  initializeTheme();

  if (themeToggle) {
    console.log('[Sidebar] Theme toggle button found, adding click handler');
    themeToggle.addEventListener('click', handleThemeToggle);
    cleanupFunctions.push(() => {
      themeToggle.removeEventListener('click', handleThemeToggle);
    });
  } else {
    console.warn(
      '[Sidebar] Theme toggle button NOT found (.sidebar-theme-toggle)',
    );
  }

  const visibilityEffectCleanup = effect(() => {
    const isVisible = isVisibleSignal.get();

    if (isVisible) {
      element.style.display = 'flex';

      void element.offsetHeight;

      element.classList.add(VISIBLE_CLASS);
      element.classList.remove(HIDDEN_CLASS);
      element.setAttribute('aria-hidden', 'false');
    } else {
      element.classList.remove(VISIBLE_CLASS);

      setTimeout(() => {
        element.classList.add(HIDDEN_CLASS);
        element.setAttribute('aria-hidden', 'true');

        const handleTransitionEnd = (e: TransitionEvent) => {
          if (
            e.propertyName === 'width' ||
            e.propertyName === 'opacity' ||
            e.propertyName === 'transform'
          ) {
            if (!isVisibleSignal.get()) {
              element.style.display = 'none';
            }
            element.removeEventListener('transitionend', handleTransitionEnd);
            pendingTransitionEnd = null;
          }
        };

        pendingTransitionEnd = handleTransitionEnd;
        element.addEventListener('transitionend', handleTransitionEnd);
      }, 20);
    }

    element.dispatchEvent(
      new CustomEvent(isVisible ? 'sidebar:visible' : 'sidebar:hidden', {
        bubbles: true,
      }),
    );
  });
  cleanupFunctions.push(visibilityEffectCleanup);

  if (isCollapsible && isMiniSignal) {
    // Collapsed stays collapsed from one page to the next.
    if (readCollapsed()) isMiniSignal.set(true);
    const miniEffectCleanup = effect(() => {
      const isMini = isMiniSignal.get();
      writeCollapsed(isMini);
      element.classList.toggle(MINI_CLASS, isMini);
      collapseToggle?.classList.toggle(MINI_CLASS, isMini);
      themeToggle?.classList.toggle(MINI_CLASS, isMini);
      /*
        Collapsed, the button's words are hidden and its name is all a reader
        has; open, its name has to contain them. Both are said here so the two
        never disagree.
      */
      collapseToggle?.setAttribute(
        'aria-label',
        isMini ? 'Expand sidebar' : translate('ui.sidebar.collapse'),
      );
      element.dispatchEvent(
        new CustomEvent('sidebar:mini-change', {
          bubbles: true,
          detail: { isMini },
        }),
      );
    });
    cleanupFunctions.push(miniEffectCleanup);
  }

  const handleToggleMini: ActionHandler = (_, event) => {
    event.preventDefault();
    const newMiniState = !isMiniSignal.get();
    isMiniSignal.set(newMiniState);

    element.classList.toggle(MINI_CLASS, newMiniState);
  };

  if (collapseToggle && isMiniSignal) {
    const toggleHandler = (event: Event) => {
      handleToggleMini(collapseToggle, event);
    };
    collapseToggle.addEventListener('click', toggleHandler);
    cleanupFunctions.push(() => {
      collapseToggle.removeEventListener('click', toggleHandler);
    });
  }

  collapsibleTriggers.forEach((button) => {
    const subMenuId = button.dataset.controls;
    const subMenu = subMenuId
      ? element.querySelector<HTMLElement>(`#${subMenuId}`)
      : null;
    const icon = button.querySelector<SVGElement>(
      `.${NAV_COLLAPSIBLE_ICON_UTILITY}`,
    );

    if (!subMenu) return;

    if (button.getAttribute('aria-expanded') === 'true') {
      subMenu.style.maxHeight = `${subMenu.scrollHeight}px`;
    } else {
      subMenu.style.maxHeight = '0px';
    }

    const handleCollapsibleToggle: ActionHandler = (btn, event) => {
      event.preventDefault();
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';

      btn.setAttribute('aria-expanded', String(!isExpanded));
      icon?.classList.toggle(EXPANDED_CLASS, !isExpanded);

      if (!isExpanded) {
        subMenu.classList.add(VISIBLE_CLASS);
        subMenu.classList.remove(HIDDEN_CLASS);

        subMenu.style.maxHeight = `${subMenu.scrollHeight}px`;
      } else {
        subMenu.style.maxHeight = `${subMenu.scrollHeight}px`;

        void subMenu.offsetHeight;
        subMenu.style.maxHeight = '0px';

        const transitionEnd = () => {
          if (btn.getAttribute('aria-expanded') === 'false') {
            subMenu.classList.add(HIDDEN_CLASS);
            subMenu.classList.remove(VISIBLE_CLASS);
          }
          subMenu.removeEventListener('transitionend', transitionEnd);
        };
        subMenu.addEventListener('transitionend', transitionEnd);
      }
    };

    const onCollapsibleClick = (event: Event) =>
      handleCollapsibleToggle(button, event);
    button.addEventListener('click', onCollapsibleClick);
    cleanupFunctions.push(() =>
      button.removeEventListener('click', onCollapsibleClick),
    );
  });

  return () => {
    if (pendingTransitionEnd) {
      element.removeEventListener('transitionend', pendingTransitionEnd);
      pendingTransitionEnd = null;
    }
    for (const cleanup of cleanupFunctions) {
      cleanup();
    }
  };
};
